import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  AUTODISPLAY_ACTIVE_HTML,
  AUTODISPLAY_SELECTED_DEVICE,
  AUTODISPLAY_ACTIVE_DPI_INFO,
  AUTODISPLAY_INTERVER_TIME,
} from "../../../AtomStates";
import { useAtom } from "jotai";
import {
  viewCheckActivateStateRequest,
  viewCheckUpdateStateRequest,
} from "../../../../utils/indexedDB";

const OrganismDisplayViewer = ({ path, activateCode, activateTimestamp }) => {
  const [autoDisplayActiveHTML] = useAtom(AUTODISPLAY_ACTIVE_HTML);
  const [autoDisplayActiveDPIInfo] = useAtom(AUTODISPLAY_ACTIVE_DPI_INFO);
  const [autoDisplaySelectedDevice] = useAtom(AUTODISPLAY_SELECTED_DEVICE);
  const [autodisplayIntervalTime] = useAtom(AUTODISPLAY_INTERVER_TIME);
  const [loading, setLoading] = useState(true);
  const [contentSize, setContentSize] = useState({ width: 1200, height: 800 });

  useEffect(() => {
    const checkActivateState = async () => {
      try {
        const response = await viewCheckActivateStateRequest({
          viewId: path,
          activateCode,
          activateTimestamp: activateTimestamp,
          activateCheckTimestamp: Date.now(),
        });

        if (response && response.status === "nonActivate") {
          window.location.href = "/AutodisplayView";
        }
      } catch (error) {
        console.error(error);
        window.location.href = "/AutodisplayView";
      }
    };

    const checkUpdateState = async () => {
      try {
        const response = await viewCheckUpdateStateRequest({
          viewId: path,
          activateCode,
          activateTimestamp: activateTimestamp,
        });
        console.log("checkUpdateState", response);

        // 응답이 success인 경우 데이터 업데이트
        if (response && response.status === "success") {
          // 데이터 업데이트
        }
      } catch (error) {
        console.error(error);
      }
    };

    // 초기 실행
    checkActivateState();
    checkUpdateState();

    // 10분마다 반복 실행
    const interval = setInterval(() => {
      checkActivateState();
      checkUpdateState();
    }, autodisplayIntervalTime);

    // 컴포넌트 언마운트 시 인터벌 정리
    return () => clearInterval(interval);
  }, []);

  // console.log("autoDisplayActiveHTML", autoDisplayActiveHTML);

  // HTML에서 width/height 추출 함수
  const extractContentSize = (htmlString) => {
    if (!htmlString) return { width: 1200, height: 800 };

    // 다양한 컨테이너 클래스들을 순서대로 검색
    const containerClasses = [
      "main-poster-container",
      "main-container",
      "poster-container",
      "content-container",
      "display-container",
    ];

    // 각 컨테이너 클래스에서 크기 검색
    for (const className of containerClasses) {
      const containerMatch = htmlString.match(
        new RegExp(
          `\\.${className}\\s*{[^}]*width\\s*:\\s*(\\d+)px[^}]*height\\s*:\\s*(\\d+)px[^}]*}`
        )
      );
      if (containerMatch) {
        return {
          width: parseInt(containerMatch[1]),
          height: parseInt(containerMatch[2]),
        };
      }

      // height가 먼저 나오는 경우도 체크
      const containerMatchReverse = htmlString.match(
        new RegExp(
          `\\.${className}\\s*{[^}]*height\\s*:\\s*(\\d+)px[^}]*width\\s*:\\s*(\\d+)px[^}]*}`
        )
      );
      if (containerMatchReverse) {
        return {
          width: parseInt(containerMatchReverse[2]),
          height: parseInt(containerMatchReverse[1]),
        };
      }
    }

    // body에서 크기 검색
    const bodyMatch = htmlString.match(
      /body\s*{[^}]*width\s*:\s*(\d+)px[^}]*height\s*:\s*(\d+)px[^}]*}/
    );
    if (bodyMatch) {
      return {
        width: parseInt(bodyMatch[1]),
        height: parseInt(bodyMatch[2]),
      };
    }

    // body에서 height가 먼저 나오는 경우도 체크
    const bodyMatchReverse = htmlString.match(
      /body\s*{[^}]*height\s*:\s*(\d+)px[^}]*width\s*:\s*(\d+)px[^}]*}/
    );
    if (bodyMatchReverse) {
      return {
        width: parseInt(bodyMatchReverse[2]),
        height: parseInt(bodyMatchReverse[1]),
      };
    }

    return { width: 1200, height: 800 };
  };

  // HTML 콘텐츠가 변경될 때 처리
  useEffect(() => {
    if (autoDisplayActiveHTML) {
      setLoading(true);

      // Google Fonts 로드
      const link = document.createElement("link");
      link.href =
        "https://fonts.googleapis.com/css2?family=Pretendard:wght@400;600;700&display=swap";
      link.rel = "stylesheet";
      if (!document.querySelector(`link[href="${link.href}"]`)) {
        document.head.appendChild(link);
      }

      const size = extractContentSize(autoDisplayActiveHTML);
      setContentSize(size);

      setLoading(false);
    }
  }, [autoDisplayActiveHTML]);

  // 로딩 상태
  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>콘텐츠를 불러오는 중...</LoadingText>
        <LoadingSubText>HTML을 가져와 파싱하고 있습니다</LoadingSubText>
      </LoadingContainer>
    );
  }

  return (
    <FullScreenContainer>
      {autoDisplayActiveHTML && (
        <OriginalHtmlFrame
          srcDoc={autoDisplayActiveHTML}
          title="HTML Display"
          style={{
            width: contentSize.width,
            height: contentSize.height,
            display: "block",
            margin: "0 auto",
          }}
        />
      )}
    </FullScreenContainer>
  );
};

export default OrganismDisplayViewer;

// 스타일 컴포넌트들
const FullScreenContainer = styled.div`
  // width: 100vw;
  min-height: 100vh;
  margin: 0;
  padding: 0;
  background: white;
  overflow: visible; /* 또는 생략 */
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

// 원본 크기 그대로 표시하는 iframe (width/height는 style로만 제어)
const OriginalHtmlFrame = styled.iframe`
  border: none;
  background: white;
  display: block;
  height: auto;
`;

const LoadingContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  z-index: 9999;
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid #333;
  border-top: 4px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  color: black;
  font-size: 18px;
  font-weight: 600;
`;

const LoadingSubText = styled.div`
  color: #666;
  font-size: 14px;
  text-align: center;
`;
